/**
 * Equipment Maintenance Service
 * Handles bidirectional integration between Equipment Register and Maintenance jobs
 *
 * Features:
 * - Automatic equipment-to-job linking based on filters
 * - Due date calculations per equipment item
 * - Service history tracking with write-back
 * - Roll-up summaries for job cards
 * - Support for individual, group, and simple tracking modes
 */

import { serviceTypeFromJob } from './maintenance/serviceType';

// Helper function for equipment type matching
function matchesEquipmentType(equipment, filterType) {
  const equipmentType = (equipment.type || equipment.label || equipment.name || '').toLowerCase();
  const filterLower = filterType.toLowerCase();

  // Check for direct match or contains
  return equipmentType === filterLower ||
         equipmentType.includes(filterLower) ||
         filterLower.includes(equipmentType);
}

/**
 * Map job ID to service type for service history
 * UPDATED: Now uses normalized service types from serviceType.js
 */
function mapJobToServiceType(job) {
  const serviceType = serviceTypeFromJob(job);
  return [serviceType]; // Return as array for backwards compatibility
}

/**
 * Add days to a date string (ISO format)
 */
function addDays(dateStr, days) {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}

/**
 * Check if equipment is due for a specific maintenance job
 *
 * @param {Object} job - Maintenance job configuration
 * @param {Object} equipment - Equipment item
 * @returns {boolean} - True if equipment needs this service
 */
export function isDueFor(job, equipment) {
  if (!equipment.serviceHistory || equipment.serviceHistory.length === 0) {
    return true; // No history = due
  }

  const serviceTypes = mapJobToServiceType(job);

  // Find the most recent service of this type
  const relevantServices = equipment.serviceHistory
    .filter(h => serviceTypes.includes(h.serviceType))
    .sort((a, b) => (b.date > a.date ? 1 : -1));

  if (relevantServices.length === 0) {
    return true; // No relevant history = due
  }

  const lastService = relevantServices[0];

  if (!lastService.nextDue) {
    return true; // No next due date = due
  }

  return new Date(lastService.nextDue) <= new Date();
}

/**
 * Get the next due date for equipment based on service history
 *
 * @param {Object} equipment - Equipment item
 * @param {Object} job - Maintenance job
 * @returns {string|null} - ISO date string or null
 */
function getNextDue(equipment, job) {
  if (!equipment.serviceHistory || equipment.serviceHistory.length === 0) {
    return null;
  }

  const serviceTypes = mapJobToServiceType(job);

  const relevantServices = equipment.serviceHistory
    .filter(h => serviceTypes.includes(h.serviceType))
    .sort((a, b) => (b.date > a.date ? 1 : -1));

  if (relevantServices.length === 0) {
    return null;
  }

  return relevantServices[0].nextDue || null;
}

/**
 * Summarize job status based on linked equipment
 * Returns counts, dates, and service history for displaying on job cards
 *
 * FT-140 FIX: Now accepts maintenanceHistory as PRIMARY source of truth.
 * maintenance_history is always written to on completion - job.completions[] is stale.
 * No migrations needed - just read from the correct source.
 *
 * @param {Object} job - Maintenance job with linkedEquipmentIds
 * @param {Map} equipmentById - Map of equipment ID to equipment object
 * @param {Array} maintenanceHistory - Optional maintenance_history array (SOURCE OF TRUTH)
 * @returns {Object} - { itemsTotal, itemsDue, nextDue, lastCompleted, dueItems }
 */
export function summarizeJob(job, equipmentById, maintenanceHistory = []) {
  // FT-140 FIX: Use maintenance_history as PRIMARY source of truth for completions
  // maintenance_history is always written to on completion - it has all the correct data
  // job.completions[] is a stale copy that falls out of sync - do NOT use as primary

  // Get linked equipment for item counts
  const items = (job.linkedEquipmentIds || [])
    .map(id => equipmentById.get(id))
    .filter(Boolean)
    .filter(eq => !eq.archived);

  // FT-140: Find completions from maintenance_history FIRST (source of truth)
  // Match by jobId and require a completion timestamp
  const historyCompletions = (maintenanceHistory || [])
    .filter(record => record.jobId === job.id && (record.completedAt || record.date))
    .sort((a, b) => {
      // Sort by most recent first
      const aTime = new Date(a.completedAt || a.date).getTime();
      const bTime = new Date(b.completedAt || b.date).getTime();
      return bTime - aTime;
    });

  // Use maintenance_history if available, otherwise fall back to job.completions[]
  // (for backwards compatibility during transition)
  const latestCompletion = historyCompletions.length > 0
    ? historyCompletions[0]
    : (job.completions || []).sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))[0];

  if (latestCompletion) {
    // Job has been completed at least once
    const nextDueDate = latestCompletion.nextDue ? new Date(latestCompletion.nextDue) : null;
    // FT-140 FIX: Use user-entered `date` field first, fall back to `completedAt` (system timestamp)
    // This ensures Dashboard and PlannedMaintenanceTab use the same date source
    const lastCompletedDate = latestCompletion.date
      ? new Date(latestCompletion.date)
      : (latestCompletion.completedAt ? new Date(latestCompletion.completedAt) : null);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let daysUntilDue = null;
    let status = 'compliant';
    let itemsDue = 0;

    if (nextDueDate) {
      const dueDateNormalized = new Date(nextDueDate);
      dueDateNormalized.setHours(0, 0, 0, 0);
      daysUntilDue = Math.ceil((dueDateNormalized - today) / (1000 * 60 * 60 * 24));

      if (daysUntilDue < 0) {
        status = 'overdue';
        itemsDue = items.length; // All items overdue
      } else if (daysUntilDue <= 7) {
        status = 'due_soon';
        itemsDue = items.length; // All items due soon
      }
    }

    return {
      itemsTotal: items.length,
      itemsDue,
      nextDue: latestCompletion.nextDue,
      // FT-140 FIX: Return `date` (YYYY-MM-DD user-entered) over `completedAt` (ISO timestamp)
      // This avoids timezone conversion issues when displaying dates
      lastCompleted: latestCompletion.date || latestCompletion.completedAt,
      performedBy: latestCompletion.performedBy,
      notes: latestCompletion.notes,
      result: latestCompletion.result,
      daysUntilDue,
      status,
      // FT-140: Use historyCompletions count when available (source of truth)
      completionCount: historyCompletions.length > 0 ? historyCompletions.length : (job.completions?.length || 0),
      // Legacy field for compatibility
      dueItems: itemsDue > 0 ? items.map(item => ({
        id: item.id,
        name: item.name || item.type,
        location: item.location
      })) : []
    };
  }

  // No completions yet - job has never been completed
  return {
    itemsTotal: items.length,
    itemsDue: items.length, // All items are due (never completed)
    nextDue: null,
    lastCompleted: null,
    performedBy: null,
    notes: null,
    result: null,
    daysUntilDue: null,
    status: 'never_completed',
    completionCount: 0,
    dueItems: items.map(item => ({
      id: item.id,
      name: item.name || item.type,
      location: item.location
    }))
  };
}

/**
 * Link equipment to jobs based on equipment filters
 * Auto-linking when equipment is added or jobs are configured
 *
 * @param {Array} equipmentItems - All equipment items
 * @param {Array} maintenanceJobs - All maintenance jobs
 * @returns {Array} - Jobs with updated linkedEquipmentIds
 */
export function linkEquipmentToJobs(equipmentItems, maintenanceJobs) {
  return maintenanceJobs.map(job => {
    if (!job.equipmentFilter) {
      return job; // No filter = no auto-linking
    }

    const { category, types, locations } = job.equipmentFilter;

    // Find equipment that matches this job's filter
    const matchingEquipment = equipmentItems.filter(eq => {
      if (eq.archived) return false;

      // Check category match
      if (category && eq.category !== category) {
        return false;
      }

      // Check type match (use flexible matching from equipmentJobLinking)
      if (types && types.length > 0) {
        const typeMatches = types.some(type => matchesEquipmentType(eq, type));
        if (!typeMatches) {
          return false;
        }
      }

      // Check location match (if specified)
      if (locations && locations.length > 0) {
        if (!eq.location || !locations.includes(eq.location)) {
          return false;
        }
      }

      return true;
    });

    // Merge with existing linked IDs (preserve manual links)
    const existingIds = new Set(job.linkedEquipmentIds || []);
    const matchingIds = matchingEquipment.map(eq => eq.id);
    const allIds = [...new Set([...existingIds, ...matchingIds])];

    return {
      ...job,
      linkedEquipmentIds: allIds
    };
  });
}

/**
 * Complete a maintenance job and write back to equipment service history
 *
 * @param {Object} job - Maintenance job
 * @param {Object} payload - Completion data
 * @param {string} payload.date - ISO date string
 * @param {string} payload.inspector - Person who completed the work
 * @param {Array<string>} payload.itemsCompleted - Equipment IDs (for individual mode)
 * @param {boolean} payload.allChecked - All items checked (for group mode)
 * @param {string} payload.notes - Completion notes
 * @param {string} payload.result - 'Pass' or 'Fail'
 * @param {Function} updateEquipmentFn - Function to update equipment (eq, updates, fromMaintenance)
 * @returns {Promise<void>}
 */
export async function completeJob(job, payload, updateEquipmentFn) {
  const serviceType = mapJobToServiceType(job)[0];
  const nextDue = addDays(payload.date, job.frequencyDays || 365);

  let equipmentIds = [];

  // Determine which equipment to update based on tracking mode
  switch (job.trackingMode) {
    case 'individual':
      // Individual mode: Only update items explicitly checked
      equipmentIds = payload.itemsCompleted || [];
      break;

    case 'group':
      // Group mode: If allChecked, update all linked items
      // Otherwise, only update explicitly selected items
      if (payload.allChecked) {
        equipmentIds = job.linkedEquipmentIds || [];
      } else {
        equipmentIds = payload.itemsCompleted || [];
      }
      break;

    case 'simple':
      // Simple mode: No equipment write-back, just record completion
      equipmentIds = [];
      break;

    default:
      equipmentIds = [];
  }

  // Update service history for each equipment item
  for (const id of equipmentIds) {
    const serviceHistoryEntry = {
      serviceType,
      jobId: job.id,
      date: payload.date,
      result: payload.result || 'Pass',
      notes: payload.notes || '',
      nextDue
    };

    await updateEquipmentFn(id, serviceHistoryEntry, true); // true = fromMaintenance flag
  }
}

/**
 * Find maintenance jobs that should be linked to a new equipment item
 * Used for auto-suggest when adding equipment
 *
 * @param {Object} equipment - New equipment item
 * @param {Array} maintenanceJobs - All maintenance jobs
 * @returns {Array} - Jobs that match this equipment
 */
export function findMatchingJobs(equipment, maintenanceJobs) {
  return maintenanceJobs.filter(job => {
    if (!job.equipmentFilter) return false;

    const { category, types, locations } = job.equipmentFilter;

    // Check category match
    if (category && equipment.category !== category) {
      return false;
    }

    // Check type match
    if (types && types.length > 0) {
      const typeMatches = types.some(type => matchesEquipmentType(equipment, type));
      if (!typeMatches) {
        return false;
      }
    }

    // Check location match (if specified)
    if (locations && locations.length > 0) {
      if (!equipment.location || !locations.includes(equipment.location)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Get equipment items that have no linked maintenance jobs
 * Returns orphaned equipment for displaying warnings
 *
 * @param {Array} equipmentItems - All equipment items
 * @param {Array} maintenanceJobs - All maintenance jobs
 * @returns {Array} - Equipment with no linked jobs
 */
export function getOrphanedEquipment(equipmentItems, maintenanceJobs) {
  // Create a set of all equipment IDs that are linked to jobs
  const linkedIds = new Set();
  maintenanceJobs.forEach(job => {
    if (job.linkedEquipmentIds) {
      job.linkedEquipmentIds.forEach(id => linkedIds.add(id));
    }
  });

  // Find equipment not in the linked set
  return equipmentItems.filter(eq => !eq.archived && !linkedIds.has(eq.id));
}

export default {
  isDueFor,
  summarizeJob,
  linkEquipmentToJobs,
  completeJob,
  findMatchingJobs,
  getOrphanedEquipment,
  mapJobToServiceType,
  addDays
};
